import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { ToastFeedback } from "Components/atoms/ToastFeedback";
import { ConfirmationModal } from "Components/molecules/ConfirmationModal";
import { FooterTemplate } from "Components/templates/FooterTemplate";
import { HeaderTemplate } from "Components/templates/Header";
import { validateAccountDeletion } from "shared/auth/auth";
import { sendGTMEvent, sendGTMEventAndWait } from "shared/services/GTM";
import { getPlatformType } from "shared/utils/platform";
import { GlobalStyles } from "styles/globalStyles";
import { theme } from "styles/theme";
import {
  Card,
  Content,
  DeleteButton,
  Field,
  HeaderArea,
  Input,
  Label,
  Page,
  Title,
  Warning,
} from "./styles";

const DELETE_CONFIRMATION = "Excluir";

export default function DeleteAccountRequestPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [confirmation, setConfirmation] = useState("");
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const confirmationViewTrackedRef = useRef(false);

  const isConfirmed =
    confirmation.trim().toLocaleLowerCase("pt-BR") ===
    DELETE_CONFIRMATION.toLocaleLowerCase("pt-BR");

  useEffect(() => {
    if (!confirmationViewTrackedRef.current) {
      confirmationViewTrackedRef.current = true;
      sendGTMEvent("accountDeletionConfirmationView", {
        platform: getPlatformType(),
      });
    }

    const existingPolicy = document.querySelector<HTMLMetaElement>(
      'meta[name="referrer"]',
    );
    const previousContent = existingPolicy?.content;
    const policy = existingPolicy ?? document.createElement("meta");

    policy.name = "referrer";
    policy.content = "no-referrer";
    if (!existingPolicy) document.head.appendChild(policy);

    return () => {
      if (existingPolicy && previousContent !== undefined) {
        existingPolicy.content = previousContent;
      } else {
        policy.remove();
      }
    };
  }, []);

  const handleDeleteAccount = async () => {
    if (!id || !isConfirmed || submitting) return;

    setSubmitting(true);
    setFeedback(null);

    try {
      await validateAccountDeletion(id);
      await sendGTMEventAndWait("accountDeletionComplete", {
        platform: getPlatformType(),
        result: "success",
      });
      window.location.assign("/");
    } catch (error) {
      sendGTMEvent("accountDeletionComplete", {
        platform: getPlatformType(),
        result: "error",
      });
      setIsConfirmationModalOpen(false);
      setFeedback({
        type: "error",
        message:
          error instanceof Error
            ? error.message
            : "Não foi possível validar a exclusão da conta.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <Page>
        <ToastFeedback
          message={feedback?.message ?? ""}
          type={feedback?.type ?? "success"}
          isOpen={Boolean(feedback)}
          onClose={() => setFeedback(null)}
        />
        <HeaderArea>
          <HeaderTemplate />
        </HeaderArea>
        <Content>
          <Card>
            <Title>Apagar minha conta</Title>
            <Warning>
              Sua conta, seus dados pessoais e todas as suas preferências serão
              apagados permanentemente da base de dados do Voopter. Esta ação é
              irreversível e você deixará de receber nossos alertas de
              passagens.
            </Warning>
            <Field>
              <Label htmlFor="delete-account-confirmation">
                Digite “Excluir” para confirmar
              </Label>
              <Input
                id="delete-account-confirmation"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                autoComplete="off"
                placeholder={DELETE_CONFIRMATION}
              />
            </Field>
            <DeleteButton
              type="button"
              disabled={!id || !isConfirmed || submitting}
              onClick={() => {
                sendGTMEvent("accountDeletionConfirm", {
                  platform: getPlatformType(),
                });
                setIsConfirmationModalOpen(true);
              }}
            >
              Apagar minha conta
            </DeleteButton>
          </Card>
        </Content>
        <FooterTemplate />

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          title="Apagar minha conta permanentemente"
          confirmLabel="Confirmar exclusão permanente"
          loadingLabel="Apagando..."
          isLoading={submitting}
          onCancel={() => {
            sendGTMEvent("accountDeletionCancel", {
              platform: getPlatformType(),
              stage: "permanent_confirmation",
            });
            setIsConfirmationModalOpen(false);
          }}
          onConfirm={() => void handleDeleteAccount()}
        >
          Ao confirmar, sua conta, seus dados pessoais, suas preferências e seus
          alertas de passagens serão apagados permanentemente da base de dados
          do Voopter. Esta ação é irreversível e você será desconectado de todos
          os dispositivos.
        </ConfirmationModal>
      </Page>
    </ThemeProvider>
  );
}
